import { defineContent, type DefinedContent } from '@post-office/define-content';

import type { TargetProduct } from '../types';

/**
 * The cjn messages are per-product variances of the same placement. Each recommended product gets
 * its own content map so the reranker's `role: 'title'` reflects the product actually being
 * recommended (Loom / Confluence / Jira Product Discovery) rather than a shared default. This keeps
 * ML Feature uploads correct per message template.
 *
 * The non-productName copy is identical across products (it fills the {productName} placeholder at
 * runtime), so it is repeated inline in every map — descriptors pulled from a variable or spread are
 * invisible to the i18n extractor.
 */
type CjnContentFields = [
	'productName',
	'facePileLabel',
	'descriptionTeam',
	'descriptionSingle',
	'descriptionMultiple',
	'reasonTeamSize',
	'reasonExistingProducts',
	'reasonsModalMessage',
	'requestSentFlagTitle',
	'requestSentFlagDescription',
	'cta',
];

export const registeredContentLoom: DefinedContent<CjnContentFields> = defineContent({
	default: {
		productName: {
			id: 'puma.common.side-nav.loom.product-name',
			description: 'Product name for Loom, shown inline in the cross-join recommendation',
			defaultMessage: 'Loom',
			role: 'title',
		},
		facePileLabel: {
			id: 'puma.common.side-nav.face-pile.label',
			description: 'Accessible label for the face pile of collaborators using the product',
			defaultMessage: 'Collaborators using this product',
		},
		descriptionTeam: {
			id: 'puma.common.side-nav.description.team',
			description:
				'Cross-join recommendation copy shown when the user has no named collaborators on the recommended product',
			defaultMessage: 'Your team is using <InlineBlock><Logo></Logo> {productName}</InlineBlock>',
			role: 'description',
		},
		descriptionSingle: {
			id: 'puma.common.side-nav.description.single',
			description:
				"Cross-join recommendation copy shown when exactly one of the user's collaborators is using the recommended product",
			defaultMessage:
				'{firstCollaborator} is using <InlineBlock><Logo></Logo> {productName}</InlineBlock>',
		},
		descriptionMultiple: {
			id: 'puma.common.side-nav.description.multiple',
			description:
				"Cross-join recommendation copy shown when two or more of the user's collaborators are using the recommended product",
			defaultMessage:
				'{firstCollaborator} and {collaboratorCount, plural, one {# other} other {# others}} are using <InlineBlock><Logo></Logo> {productName}</InlineBlock>',
		},
		reasonTeamSize: {
			id: 'puma.common.cjn-nav4.reasons-modal.team-size',
			description:
				"Reason explaining that the recommendation is based on the size of the user's team",
			defaultMessage: 'the size of your team',
		},
		reasonExistingProducts: {
			id: 'puma.common.cjn-nav4.reasons-modal.existing-products',
			description:
				"Reason explaining that the recommendation is based on the user's existing Atlassian products",
			defaultMessage: 'your existing Atlassian products',
		},
		reasonsModalMessage: {
			id: 'puma.common.cjn-nav4.reasons-modal.message',
			description:
				"Explanation of why the recommended product may improve the user's team's productivity",
			defaultMessage:
				"We think that {productName} will improve your team's productivity. This recommendation is based on:",
		},
		requestSentFlagTitle: {
			id: 'puma.common.side-nav.request-sent-flag.title',
			description:
				'Title of the toast shown after the user successfully requests to join their team on the recommended product',
			defaultMessage: 'Nearly there!',
		},
		requestSentFlagDescription: {
			id: 'puma.common.side-nav.request-sent-flag.description',
			description:
				'Body of the toast shown after the user successfully requests to join their team on the recommended product',
			defaultMessage:
				"A quick access check is all that's left. We'll email you the moment you're good to try {productName}",
		},
		cta: {
			id: 'puma.common.side-nav.request-access-link',
			description:
				'Call-to-action link inviting the user to request access to the recommended product so they can join their team',
			defaultMessage: 'Join your team',
		},
	},
});

export const registeredContentConfluence: DefinedContent<CjnContentFields> = defineContent({
	default: {
		productName: {
			id: 'puma.common.side-nav.confluence.product-name',
			description: 'Product name for Confluence, shown inline in the cross-join recommendation',
			defaultMessage: 'Confluence',
			role: 'title',
		},
		facePileLabel: {
			id: 'puma.common.side-nav.face-pile.label',
			description: 'Accessible label for the face pile of collaborators using the product',
			defaultMessage: 'Collaborators using this product',
		},
		descriptionTeam: {
			id: 'puma.common.side-nav.description.team',
			description:
				'Cross-join recommendation copy shown when the user has no named collaborators on the recommended product',
			defaultMessage: 'Your team is using <InlineBlock><Logo></Logo> {productName}</InlineBlock>',
			role: 'description',
		},
		descriptionSingle: {
			id: 'puma.common.side-nav.description.single',
			description:
				"Cross-join recommendation copy shown when exactly one of the user's collaborators is using the recommended product",
			defaultMessage:
				'{firstCollaborator} is using <InlineBlock><Logo></Logo> {productName}</InlineBlock>',
		},
		descriptionMultiple: {
			id: 'puma.common.side-nav.description.multiple',
			description:
				"Cross-join recommendation copy shown when two or more of the user's collaborators are using the recommended product",
			defaultMessage:
				'{firstCollaborator} and {collaboratorCount, plural, one {# other} other {# others}} are using <InlineBlock><Logo></Logo> {productName}</InlineBlock>',
		},
		reasonTeamSize: {
			id: 'puma.common.cjn-nav4.reasons-modal.team-size',
			description:
				"Reason explaining that the recommendation is based on the size of the user's team",
			defaultMessage: 'the size of your team',
		},
		reasonExistingProducts: {
			id: 'puma.common.cjn-nav4.reasons-modal.existing-products',
			description:
				"Reason explaining that the recommendation is based on the user's existing Atlassian products",
			defaultMessage: 'your existing Atlassian products',
		},
		reasonsModalMessage: {
			id: 'puma.common.cjn-nav4.reasons-modal.message',
			description:
				"Explanation of why the recommended product may improve the user's team's productivity",
			defaultMessage:
				"We think that {productName} will improve your team's productivity. This recommendation is based on:",
		},
		requestSentFlagTitle: {
			id: 'puma.common.side-nav.request-sent-flag.title',
			description:
				'Title of the toast shown after the user successfully requests to join their team on the recommended product',
			defaultMessage: 'Nearly there!',
		},
		requestSentFlagDescription: {
			id: 'puma.common.side-nav.request-sent-flag.description',
			description:
				'Body of the toast shown after the user successfully requests to join their team on the recommended product',
			defaultMessage:
				"A quick access check is all that's left. We'll email you the moment you're good to try {productName}",
		},
		cta: {
			id: 'puma.common.side-nav.request-access-link',
			description:
				'Call-to-action link inviting the user to request access to the recommended product so they can join their team',
			defaultMessage: 'Join your team',
		},
	},
});

export const registeredContentJpd: DefinedContent<CjnContentFields> = defineContent({
	default: {
		productName: {
			id: 'puma.common.side-nav.jpd.product-name',
			description:
				'Product name for Jira Product Discovery, shown inline in the cross-join recommendation',
			defaultMessage: 'Jira Product Discovery',
			role: 'title',
		},
		facePileLabel: {
			id: 'puma.common.side-nav.face-pile.label',
			description: 'Accessible label for the face pile of collaborators using the product',
			defaultMessage: 'Collaborators using this product',
		},
		descriptionTeam: {
			id: 'puma.common.side-nav.description.team',
			description:
				'Cross-join recommendation copy shown when the user has no named collaborators on the recommended product',
			defaultMessage: 'Your team is using <InlineBlock><Logo></Logo> {productName}</InlineBlock>',
			role: 'description',
		},
		descriptionSingle: {
			id: 'puma.common.side-nav.description.single',
			description:
				"Cross-join recommendation copy shown when exactly one of the user's collaborators is using the recommended product",
			defaultMessage:
				'{firstCollaborator} is using <InlineBlock><Logo></Logo> {productName}</InlineBlock>',
		},
		descriptionMultiple: {
			id: 'puma.common.side-nav.description.multiple',
			description:
				"Cross-join recommendation copy shown when two or more of the user's collaborators are using the recommended product",
			defaultMessage:
				'{firstCollaborator} and {collaboratorCount, plural, one {# other} other {# others}} are using <InlineBlock><Logo></Logo> {productName}</InlineBlock>',
		},
		reasonTeamSize: {
			id: 'puma.common.cjn-nav4.reasons-modal.team-size',
			description:
				"Reason explaining that the recommendation is based on the size of the user's team",
			defaultMessage: 'the size of your team',
		},
		reasonExistingProducts: {
			id: 'puma.common.cjn-nav4.reasons-modal.existing-products',
			description:
				"Reason explaining that the recommendation is based on the user's existing Atlassian products",
			defaultMessage: 'your existing Atlassian products',
		},
		reasonsModalMessage: {
			id: 'puma.common.cjn-nav4.reasons-modal.message',
			description:
				"Explanation of why the recommended product may improve the user's team's productivity",
			defaultMessage:
				"We think that {productName} will improve your team's productivity. This recommendation is based on:",
		},
		requestSentFlagTitle: {
			id: 'puma.common.side-nav.request-sent-flag.title',
			description:
				'Title of the toast shown after the user successfully requests to join their team on the recommended product',
			defaultMessage: 'Nearly there!',
		},
		requestSentFlagDescription: {
			id: 'puma.common.side-nav.request-sent-flag.description',
			description:
				'Body of the toast shown after the user successfully requests to join their team on the recommended product',
			defaultMessage:
				"A quick access check is all that's left. We'll email you the moment you're good to try {productName}",
		},
		cta: {
			id: 'puma.common.side-nav.request-access-link',
			description:
				'Call-to-action link inviting the user to request access to the recommended product so they can join their team',
			defaultMessage: 'Join your team',
		},
	},
});

/**
 * Lookup used by the shared render components to resolve the right per-product content map from the
 * `targetProduct` prop.
 */
export const registeredContentByProduct: Record<TargetProduct, typeof registeredContentLoom> = {
	loom: registeredContentLoom,
	'jira-product-discovery': registeredContentJpd,
	confluence: registeredContentConfluence,
};
